import {
  ChevronDownIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid'
import { cn, Progress, ScrollShadow } from '@heroui/react'
import { FileStatus, MediaType } from '@qse/api'
import { AnimatePresence, motion } from 'framer-motion'
import React, { ReactNode, useState } from 'react'

import { TruncatedTooltip } from './TruncatedTooltip'

// Common icons
export const ErrorIcon = () => (
  <ExclamationTriangleIcon className="size-6 text-red-600 dark:text-[#DD524C]" />
)

export const AbortIcon = () => (
  <ExclamationCircleIcon className="size-6 text-gray-900 dark:text-white" />
)
export const CheckIcon = () => (
  <img alt="check" className="w-6 h-6" src="/assets/svg/check-box-true.svg" />
)
export const SpinnerIcon = () => (
  <div className="w-6 h-6 flex items-center justify-center">
    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
  </div>
)

// Common file interface that both upload and download items should extend
export interface BaseProgressItem {
  id: string | number
  name: string
  status: string
  mediaType?: MediaType
  progress?: number
  errorMessage?: string

  // ✅ add these (optional) for folders
  totalFiles?: number
  completedFiles?: number
}

// Configuration for different tracker types
export interface TrackerConfig<T extends BaseProgressItem> {
  type: 'upload' | 'download'
  title: string
  titleColor: string
  actionButton?: {
    text: string
    action: () => void
  }
  onFolderClick?: (item: T) => void
  getLeftIcon: (item: T) => ReactNode
  getRightIcon: (item: T) => ReactNode
  renderProgressContent?: (item: T) => ReactNode
  renderStatusContent?: (item: T) => ReactNode
  getFileName: (item: T, hoveredId: string | number | null) => string
  getFileTitle?: (item: T) => string
}

export interface SharedProgressTrackerProps<T extends BaseProgressItem> {
  isMobile?: boolean
  items: T[]
  isOpen: boolean
  isExpanded: boolean
  config: TrackerConfig<T>
  onToggleExpanded: () => void
  onClose: () => void
  onRetry?: (id: string | number) => void
  className?: string
}

const SharedProgressTracker = <T extends BaseProgressItem>({
  isMobile = false,
  items,
  isOpen,
  isExpanded,
  config,
  onToggleExpanded,
  onClose,
  onRetry: _onRetry,
  className = '',
}: SharedProgressTrackerProps<T>) => {
  const [hoveredFolderId, setHoveredFolderId] = useState<string | number | null>(null)

  if (!isOpen || !items || items.length === 0) {
    return null
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClose()
  }

  const handleFolderClick = (item: T) => {
    if (config.onFolderClick) {
      config.onFolderClick(item)
    }
  }

  return (
    <motion.div
      className={`${isMobile ? 'max-w-[340px]' : 'md:w-[500px] xl:w-[600px] rounded-tl-xl rounded-tr-xl bg-white dark:!bg-[#323232] border border-gray-200 dark:border-gray-700 shadow-lg'} z-50 cursor-pointer ${className}`}
      transition={isMobile ? {} : { type: 'spring', stiffness: 400, damping: 17 }}
      whileHover={isMobile ? {} : isExpanded ? { scale: 1 } : { scale: 1.02 }}
      whileTap={isMobile ? {} : isExpanded ? { scale: 1 } : { scale: 0.98 }}
      onClick={isMobile ? undefined : onToggleExpanded}
    >
      <div className={cn('', isMobile ? 'p-2' : 'p-3')}>
        {/* Header */}
        <div className="flex items-center justify-between">
          {/* <h3 className={`text-sm font-medium ${config.titleColor}`}>{config.title}</h3> */}
          <h3 className={`text-sm font-medium`}>
            {config.type === 'upload' ? 'Upload Tracker' : 'Download Tracker'}
          </h3>
          <div className="flex items-center gap-2">
            {/* Expand/Collapse Arrow */}
            {!isMobile && (
              <motion.button
                animate={isMobile ? {} : { rotate: isExpanded ? 0 : 180 }}
                className="p-1 w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-200 group"
                title={isExpanded ? 'Collapse' : 'Expand'}
                transition={isMobile ? {} : { duration: 0.2 }}
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onToggleExpanded()
                }}
              >
                <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-300 group-hover:text-gray-700 dark:group-hover:text-white transition-colors duration-200" />
              </motion.button>
            )}

            {/* Close Button */}
            <button className="p-0 w-6 h-6" title="Close" type="button" onClick={handleClose}>
              <XCircleIcon className="w-6 h-6 text-gray-500 hover:text-gray-700 dark:hover:text-white dark:text-gray-300 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors duration-200" />
            </button>
          </div>
        </div>

        {/* Expandable Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              animate={isMobile ? {} : { opacity: 1, height: 'auto' }}
              className="mt-3 space-y-2"
              exit={isMobile ? {} : { opacity: 0, height: 0 }}
              initial={isMobile ? {} : { opacity: 0, height: 0 }}
              transition={isMobile ? {} : { duration: 0.3, ease: 'easeInOut' }}
            >
              {/* Action Banner (for uploads "Starting upload...") */}
              {config.actionButton && (
                <motion.div
                  animate={isMobile ? {} : { opacity: 1, y: 0 }}
                  className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-2"
                  initial={isMobile ? {} : { opacity: 0, y: -10 }}
                  transition={isMobile ? {} : { delay: 0 }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse-slow" />
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      {config.type === 'upload' ? 'Starting upload...' : 'Processing...'}
                    </span>
                  </div>
                  <button
                    className="text-red-600 dark:text-[#DD524C] text-sm hover:underline transition-all duration-300 font-medium"
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      config.actionButton!.action()
                    }}
                  >
                    {config.actionButton.text}
                  </button>
                </motion.div>
              )}
              {/* File Items */}
              <ScrollShadow
                hideScrollBar
                className="max-h-[470px] flex flex-col gap-3 overflow-y-auto"
              >
                {items.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    animate={isMobile ? {} : { opacity: 1, y: 0 }}
                    className={`flex items-center gap-3 lg:gap-4 ${idx === 0 ? '' : 'mt-1'}`}
                    initial={isMobile ? {} : { opacity: 0, y: -10 }}
                    role="listitem"
                    transition={isMobile ? {} : { delay: 0.05 * idx }}
                  >
                    {/* Left Icon */}
                    {config.getLeftIcon(item as T)}

                    {/* File Content */}
                    <div className="flex-1 flex flex-col min-w-0">
                      {/* File Name */}
                      <TruncatedTooltip
                        className="max-w-[600px] break-all"
                        content={config.getFileTitle?.(item as T) || item.name}
                      >
                        <div
                          className={cn(
                            'truncate text-sm font-medium transition-all duration-200 text-gray-900 dark:text-white',
                            item.mediaType === MediaType.FOLDER &&
                              (item.status === FileStatus.AVAILABLE ||
                                item.status === 'AVAILABLE') &&
                              config.onFolderClick &&
                              // Only show clickable style if navigation is possible
                              ((item as any).folderUploadId ||
                                ((item as any).parentPath && (item as any).parentPath !== '/')) &&
                              'cursor-pointer hover:text-primary-500 dark:hover:text-primary-400 hover:underline',
                          )}
                          onClick={e => {
                            if (
                              item.mediaType === MediaType.FOLDER &&
                              (item.status === FileStatus.AVAILABLE ||
                                item.status === 'AVAILABLE') &&
                              config.onFolderClick &&
                              // Only allow click if navigation is possible
                              ((item as any).folderUploadId ||
                                ((item as any).parentPath && (item as any).parentPath !== '/'))
                            ) {
                              e.stopPropagation()
                              handleFolderClick(item as T)
                            }
                          }}
                          onMouseEnter={() => {
                            if (
                              item.mediaType === MediaType.FOLDER &&
                              (item.status === FileStatus.AVAILABLE || item.status === 'AVAILABLE')
                            ) {
                              setHoveredFolderId(item.id)
                            }
                          }}
                          onMouseLeave={() => {
                            if (item.mediaType === MediaType.FOLDER) {
                              setHoveredFolderId(null)
                            }
                          }}
                        >
                          {item.mediaType === MediaType.FOLDER
                            ? `${config.getFileName(item as T, hoveredFolderId)}`
                            : config.getFileName(item as T, item.id)}
                        </div>
                      </TruncatedTooltip>

                      {/* Progress Content (upload progress bars, etc.) */}
                      {config.renderProgressContent?.(item as T)}

                      {/* Status Content (encryption, decryption status, etc.) */}
                      {config.renderStatusContent ? (
                        config.renderStatusContent(item as T)
                      ) : (
                        <StatusIndicator
                          mediaType={item.mediaType}
                          status={item.status}
                          type={config.type}
                        />
                      )}
                    </div>

                    {/* Right Icon */}
                    {config.getRightIcon(item as T)}

                    {/* Retry Button */}
                    {/* {isFailedStatus(item.status) && onRetry && (
                      <button
                        className="ml-2 text-blue-600 dark:text-[#4793EB] text-xs hover:underline transition-all duration-300"
                        onClick={e => {
                          e.stopPropagation()
                          onRetry(item.id)
                        }}
                      >
                        Retry
                      </button>
                    )} */}
                  </motion.div>
                ))}
              </ScrollShadow>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

// Upload-specific progress bar component
export const UploadProgressBar: React.FC<{
  file: BaseProgressItem & {
    progress?: number
    uploadStrategy?: string
    chunks?: number
    currentChunk?: number
    eta?: number
    uploadedFiles?: number
    totalFiles?: number
    encryptedFiles?: number
    failedUploads?: number
    failedEncryptions?: number
    failedFiles?: number
    timeEstimate?: string
    // Encryption progress tracking (for individual files)
    encryptionProgress?: number
    encryptionBytesProcessed?: number
    encryptionTotalBytes?: number
    encryptionThroughput?: number
    encryptionEta?: number
  }
}> = ({ file }) => {
  const isUploading = file.status === FileStatus.UPLOADING || file.status === 'UPLOADING'
  const isEncrypting = file.status === FileStatus.ENCRYPTING || file.status === 'ENCRYPTING'

  // For folders, we might show both states if encryption has started on some files
  const isFolder = file.mediaType === MediaType.FOLDER
  const showBothStates = isFolder && isUploading && file.encryptedFiles && file.encryptedFiles > 0

  // Show progress bar for uploading or encrypting states
  // Hide progress bar when fully completed to clean up UI
  const shouldShowProgress = isUploading || isEncrypting

  // Show component only for uploading or encrypting states
  const shouldShowComponent = shouldShowProgress || showBothStates

  if (!shouldShowComponent) return null

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`

    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`
  }

  const getStatusMessages = () => {
    const messages = []

    if (isFolder) {
      // Enhanced folder messages with combined upload and encryption progress
      const uploadProgress = file.uploadedFiles !== undefined && file.totalFiles !== undefined
      const encryptionProgress = file.encryptedFiles !== undefined && file.totalFiles !== undefined

      if (uploadProgress || encryptionProgress) {
        let statusText = ''

        // Add upload progress
        if (uploadProgress) {
          const failedText =
            file.failedUploads && file.failedUploads > 0 ? ` (${file.failedUploads} failed)` : ''

          statusText += `Upload ${file.uploadedFiles}/${file.totalFiles}${failedText}`
        }

        // Add encryption progress
        if (encryptionProgress) {
          if (statusText) statusText += ' | '

          // Show actual encryption failures (already merged from WebSocket events)
          const failedEncryptionCount = file.failedEncryptions || 0
          const failedText = failedEncryptionCount > 0 ? ` (${failedEncryptionCount} failed)` : ''

          // Use total files as denominator to show actual progress
          const totalFiles = file.totalFiles || 0

          statusText += `Encrypt ${file.encryptedFiles}/${totalFiles}${failedText}`
        }

        messages.push({
          status: statusText,
          eta: file.eta && file.eta > 0 ? formatTime(file.eta) : null,
        })
      }

      // Show overall progress for folders with real-time data from WebSocket events
      // Commented out for now as requested
      // if (file.uploadedFiles !== undefined && file.totalFiles !== undefined) {
      //   // Calculate overall progress based on uploaded files vs total files
      //   // This should match the actual folder upload progress
      //   const overallProgress = file.totalFiles > 0 ? Math.round((file.uploadedFiles / file.totalFiles) * 100) : 0

      //   if (overallProgress > 0 && overallProgress < 100) {
      //     messages.push({
      //       status: `Overall: ${overallProgress}%`,
      //       eta: null,
      //     })
      //   }
      // }
    } else {
      // Simple file messages
      if (isUploading) {
        messages.push({
          status: 'Uploading file...',
          eta: null,
        })
      }

      if (isEncrypting) {
        // Show encryption progress percentage if available (backend sends it for files >20MB)
        const encryptionPercentage = file.encryptionProgress
        const hasEncryptionProgress =
          encryptionPercentage !== undefined && encryptionPercentage !== null

        const statusText = hasEncryptionProgress
          ? `Encrypting file... ${Math.round(encryptionPercentage)}%`
          : 'Encrypting file...'

        messages.push({
          status: statusText,
        })
      }
    }

    return messages
  }

  const statusMessages = getStatusMessages()

  // Calculate the progress value to display
  const getProgressValue = () => {
    if (isFolder && file.uploadedFiles !== undefined && file.totalFiles !== undefined) {
      // For folder uploads, include failed files so progress doesn't stall
      const failedCount =
        file.failedFiles ?? (file.failedUploads ?? 0) + (file.failedEncryptions ?? 0)
      const progressed = Math.min(file.totalFiles, (file.uploadedFiles || 0) + (failedCount || 0))

      return file.totalFiles > 0 ? Math.round((progressed / file.totalFiles) * 100) : 0
    }

    // For individual files, use the file progress
    return file.progress || 0
  }

  const progressValue = getProgressValue()

  return (
    <div className="flex flex-col">
      {/* Progress Bar - Show for uploading, encrypting, or completed */}
      {shouldShowProgress && (
        <div className="flex items-center gap-1">
          <div className="w-full">
            <Progress
              aria-label="Loading..."
              color="primary"
              size="sm"
              value={progressValue}
              classNames={{
                track: 'bg-gray-200 dark:bg-gray-700 h-2',
                indicator: 'bg-primary-500 dark:bg-primary-400',
              }}
            />
          </div>

          <span className="text-[10px] text-gray-600 dark:text-gray-300 shrink-0">
            {Math.max(0, Math.min(100, progressValue))}%
          </span>
        </div>
      )}

      {/* Status Pointers - Below progress line */}
      {statusMessages.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-primary-500 dark:bg-primary-400 rounded-full animate-pulse" />
            <div className="flex items-center gap-2">
              {statusMessages.map((message, index) => (
                <React.Fragment key={index}>
                  <span className="text-[10px] text-primary-600 dark:text-primary-400">
                    {message.status}
                  </span>
                  {index < statusMessages.length - 1 && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          {/* Show time estimate for folder uploads during upload phase only (upload ETA) */}
          {/* {isFolder &&
            file.timeEstimate &&
            isUploading &&
            (() => {
              // Only show upload ETA if upload phase is NOT complete
              // Upload phase is complete when: uploadedFiles + failedUploads >= totalFiles
              const uploadedCount = file.uploadedFiles || 0
              const failedUploadCount = file.failedUploads || 0
              const totalFiles = file.totalFiles || 0
              const uploadPhaseComplete = uploadedCount + failedUploadCount >= totalFiles

              return !uploadPhaseComplete
            })() && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {file.timeEstimate}
              </span>
            )} */}
          {/* Show legacy eta if available and no time estimate */}
          {/* {!file.timeEstimate && statusMessages.find(message => message.eta)?.eta && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              ({statusMessages.find(message => message.eta)?.eta})
            </span>
          )} */}
        </div>
      )}
    </div>
  )
}

// Status indicator component
export const StatusIndicator: React.FC<{
  type: 'upload' | 'download'
  status: string
  mediaType?: MediaType
}> = ({ type, status, mediaType }) => {
  // Only handle download statuses, upload statuses are handled in UploadProgressBar
  if (type === 'upload') return null

  const isProcessing = status === 'PROCESSING' || status === 'DECRYPTING' || status === 'PREPARING'

  if (!isProcessing) return null

  const isFolder = mediaType === MediaType.FOLDER
  const message = isFolder ? 'Decrypting folder...' : 'Decrypting file...'

  return (
    <div className="flex flex-col gap-1 mt-1">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-primary-500 dark:bg-primary-400 rounded-full animate-pulse" />
        <span className="text-[10px] text-primary-600 dark:text-primary-400">{message}</span>
      </div>
    </div>
  )
}

export default SharedProgressTracker
